import { useCallback, useEffect, useRef, useState } from 'react'
import {
	MagnifyingGlassIcon,
	BuildingOfficeIcon,
	MapPinIcon,
	EnvelopeIcon,
	IdentificationIcon,
} from '@heroicons/react/20/solid'
import InfaktService from '@/services/InfaktService'

const emptyForm = {
	nazwaFirmy: '',
	nip: '',
	nazwaUlicy: '',
	nrBudynku: '',
	nrLokalu: '',
	kodPocztowy: '',
	nazwaMiejscowosci: '',
	adresEmail: '',
	infaktClientId: '',
}

const splitAddress = rawAddress => {
	const address = (rawAddress || '').trim()
	if (!address) return { streetName: '', buildingNo: '', unitNo: '' }

	const match = address.match(/^(.*?)(?:\s+(\d+[A-Za-z]?))(?:\/(\w+))?$/)
	if (!match) return { streetName: address, buildingNo: '', unitNo: '' }

	return {
		streetName: (match[1] || '').trim(),
		buildingNo: match[2] || '',
		unitNo: match[3] || '',
	}
}

export default function InfaktCustomerPicker({ onCustomerSelect, value, companyId, placowkaData }) {
	const [searchTerm, setSearchTerm] = useState('')
	const [results, setResults] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [showResults, setShowResults] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)
	const [form, setForm] = useState(value || emptyForm)
	const [actionLoading, setActionLoading] = useState(false)
	const [actionStatus, setActionStatus] = useState(null)
	const debounceRef = useRef(null)
	const wrapperRef = useRef(null)

	useEffect(() => {
		if (value) setForm(value)
	}, [value])

	const doSearch = useCallback(async query => {
		if (!query.trim() || !companyId) {
			setResults([])
			setHasSearched(false)
			setShowResults(false)
			return
		}
		setLoading(true)
		setError(null)
		setHasSearched(true)
		const { data, error: err } = await InfaktService.searchClient(query, companyId)
		if (err) {
			setError(err)
			setResults([])
			setShowResults(true)
		} else {
			setResults(data)
			setShowResults(true)
		}
		setLoading(false)
	}, [companyId])

	const handleSearchChange = useCallback(e => {
		const val = e.target.value
		setSearchTerm(val)
		if (!val.trim()) {
			setHasSearched(false)
			setShowResults(false)
		}
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => doSearch(val), 400)
	}, [doSearch])

	useEffect(() => {
		return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
	}, [])

	useEffect(() => {
		const handleClickOutside = e => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				setShowResults(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const selectResult = useCallback(client => {
		const next = {
			nazwaFirmy: client.name || client.company_name || client.nazwa || '',
			nip: client.nip || client.tax_number || '',
			nazwaUlicy: client.street || client.nazwa_ulicy || '',
			nrBudynku: client.street_number || client.nr_budynku || '',
			nrLokalu: client.flat_number || client.nr_lokalu || '',
			kodPocztowy: client.postal_code || client.kod_pocztowy || '',
			nazwaMiejscowosci: client.city || client.nazwa_miejscowosci || '',
			adresEmail: client.email || client.adres_email || '',
			infaktClientId: client.id != null ? String(client.id) : '',
		}
		setForm(next)
		setActionStatus(null)
		setShowResults(false)
		setSearchTerm('')
		if (onCustomerSelect) onCustomerSelect(next)
	}, [onCustomerSelect])

	const handleFieldChange = useCallback((field, val) => {
		const next = { ...form, [field]: val }
		setForm(next)
		setActionStatus(null)
		if (onCustomerSelect) onCustomerSelect(next)
	}, [form, onCustomerSelect])

	const handleClear = useCallback(() => {
		setForm(emptyForm)
		setSearchTerm('')
		setResults([])
		setHasSearched(false)
		setShowResults(false)
		setActionStatus(null)
		if (onCustomerSelect) onCustomerSelect(emptyForm)
	}, [onCustomerSelect])

	const handlePopulateFromPlacowka = useCallback(() => {
		if (!placowkaData) return
		const { streetName, buildingNo, unitNo } = splitAddress(placowkaData.adres)
		const next = {
			nazwaFirmy: placowkaData.nazwa || '',
			nip: placowkaData.nip || '',
			nazwaUlicy: streetName,
			nrBudynku: buildingNo,
			nrLokalu: unitNo,
			kodPocztowy: placowkaData.kod_pocztowy || placowkaData.kodPocztowy || '',
			nazwaMiejscowosci: placowkaData.id_miasto?.nazwa || placowkaData.miasto || '',
			adresEmail: placowkaData.email || placowkaData.adresEmail || placowkaData.adres_email || '',
			infaktClientId: '',
		}
		setForm(next)
		setActionStatus(null)
		if (onCustomerSelect) onCustomerSelect(next)
	}, [onCustomerSelect, placowkaData])

	const handleAddClient = useCallback(async () => {
		if (form.infaktClientId) {
			setActionStatus({ type: 'error', text: 'Klient już istnieje. Użyj „Zapisz zmiany”.' })
			return
		}
		if (!companyId) {
			setActionStatus({ type: 'error', text: 'Najpierw wybierz firmę.' })
			return
		}
		if (!form.nazwaFirmy.trim()) {
			setActionStatus({ type: 'error', text: 'Nazwa firmy jest wymagana.' })
			return
		}

		setActionLoading(true)
		setActionStatus(null)
		const result = await InfaktService.addClient(companyId, form)
		if (!result.success) {
			setActionStatus({ type: 'error', text: String(result.error || 'Nie udało się dodać klienta.') })
			setActionLoading(false)
			return
		}

		const next = { ...form, infaktClientId: result.id ? String(result.id) : form.infaktClientId }
		setForm(next)
		if (onCustomerSelect) onCustomerSelect(next)
		setActionStatus({
			type: 'success',
			text: result.id
				? `Klient został dodany. ID: ${result.id}`
				: 'Klient został dodany.',
		})
		setActionLoading(false)
	}, [companyId, form, onCustomerSelect])

	const handleSaveChanges = useCallback(async () => {
		if (!companyId) {
			setActionStatus({ type: 'error', text: 'Najpierw wybierz firmę.' })
			return
		}
		if (!form.infaktClientId) {
			setActionStatus({ type: 'error', text: 'Brak ID klienta. Wyszukaj klienta lub dodaj nowego.' })
			return
		}

		setActionLoading(true)
		setActionStatus(null)
		const result = await InfaktService.updateClient(companyId, form.infaktClientId, form)
		if (!result.success) {
			setActionStatus({ type: 'error', text: String(result.error || 'Nie udało się zapisać zmian.') })
			setActionLoading(false)
			return
		}

		const next = { ...form, infaktClientId: result.id ? String(result.id) : form.infaktClientId }
		setForm(next)
		if (onCustomerSelect) onCustomerSelect(next)
		setActionStatus({ type: 'success', text: 'Zmiany klienta zostały zapisane.' })
		setActionLoading(false)
	}, [companyId, form, onCustomerSelect])

	const fields = [
		{ key: 'nazwaFirmy', label: 'Nazwa firmy', icon: BuildingOfficeIcon, colSpan: 'sm:col-span-2' },
		{ key: 'nazwaUlicy', label: 'Nazwa ulicy', icon: MapPinIcon, colSpan: 'sm:col-span-2' },
		{ key: 'nrBudynku', label: 'Nr budynku' },
		{ key: 'nrLokalu', label: 'Nr lokalu' },
		{ key: 'kodPocztowy', label: 'Kod pocztowy' },
		{ key: 'nazwaMiejscowosci', label: 'Nazwa miejscowości' },
		{ key: 'nip', label: 'NIP', icon: IdentificationIcon },
		{ key: 'adresEmail', label: 'Adres email', icon: EnvelopeIcon, type: 'email' },
	]

	return (
		<div className="rounded-none sm:rounded-lg border border-gray-200 p-4 space-y-4">
			<div className="flex items-center justify-between gap-3">
				<div className="text-sm text-gray-600">Dane klienta Infakt</div>
				<button
					type="button"
					onClick={handlePopulateFromPlacowka}
					disabled={!placowkaData}
					className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					Uzupełnij z danych placówki
				</button>
			</div>
			{/* Search */}
			<div className="relative" ref={wrapperRef}>
				<div className="relative">
					<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						value={searchTerm}
						onChange={handleSearchChange}
						onFocus={() => hasSearched && setShowResults(true)}
						placeholder="Szukaj po nazwie lub NIP"
						className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
					/>
					{loading && (
						<div className="absolute right-3 top-1/2 -translate-y-1/2">
							<svg className="h-5 w-5 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
						</div>
					)}
				</div>

				{error && (
					<div className="mt-1 text-xs text-red-500">{String(error)}</div>
				)}

				{showResults && (
					<ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
						{results.length === 0 && !loading && !error && hasSearched && (
							<li className="px-4 py-3 text-sm text-gray-500">Nie znaleziono klienta</li>
						)}
						{results.map((client, idx) => {
							const name = client.name || client.company_name || client.nazwa || '—'
							const nip = client.nip || client.tax_number || ''
							const city = client.city || client.nazwa_miejscowosci || ''
							return (
								<li
									key={client.uuid || client.id || idx}
									className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors"
									onClick={() => selectResult(client)}
								>
									<BuildingOfficeIcon className="h-5 w-5 shrink-0 text-gray-400" />
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium text-gray-900">{name}</div>
										<div className="flex items-center gap-3 text-xs text-gray-500">
											{nip && <span>NIP: {nip}</span>}
											{city && <span>{city}</span>}
										</div>
									</div>
								</li>
							)
						})}
					</ul>
				)}
			</div>

			{/* Infakt Client ID indicator */}
			{form.infaktClientId && (
				<div className="flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 border border-indigo-100">
					<IdentificationIcon className="h-4 w-4 text-indigo-400" />
					<span className="text-xs text-indigo-400">Infakt Client ID: {form.infaktClientId}</span>
					<button
						type="button"
						onClick={handleClear}
						className="ml-auto text-xs text-red-500 hover:text-red-700 underline"
					>
						Wyczyść
					</button>
				</div>
			)}

			{/* Form fields */}
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				{fields.map(({ key, label, icon: Icon, colSpan, type }) => (
					<div key={key} className={colSpan || ''}>
						<label className="mb-1 block text-sm font-medium text-gray-700">
							{Icon && <Icon className="mr-1 inline h-4 w-4 text-gray-400" />}
							{label}
						</label>
						<input
							type={type || 'text'}
							value={form[key]}
							onChange={e => handleFieldChange(key, e.target.value)}
							className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 text-sm focus:ring-2 focus:ring-indigo-600"
						/>
					</div>
				))}
			</div>

			<div className="flex items-center gap-2">
				{!form.infaktClientId && (
					<button
						type="button"
						onClick={handleAddClient}
						disabled={actionLoading || !companyId}
						className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400"
					>
						{actionLoading ? 'Przetwarzanie...' : 'Dodaj klienta'}
					</button>
				)}
				{form.infaktClientId && (
					<button
						type="button"
						onClick={handleSaveChanges}
						disabled={actionLoading || !companyId}
						className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400"
					>
						{actionLoading ? 'Przetwarzanie...' : 'Zapisz zmiany'}
					</button>
				)}
			</div>

			{actionStatus && (
				<div
					className={`rounded-md border px-3 py-2 text-sm ${
						actionStatus.type === 'success'
							? 'border-green-200 bg-green-50 text-green-700'
							: 'border-red-200 bg-red-50 text-red-700'
					}`}
				>
					{actionStatus.text}
				</div>
			)}
		</div>
	)
}
